import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Sidebar from '../components/Sidebar';
import Header from '../components/Header';
import { useAuth } from '../context/AuthContext';
import { checkAuthBeforeAction } from '../utils/authHelper';
import api from '../services/api';

const Blackboard = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [formData, setFormData] = useState({
    category: 'general',
    subject: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState({ type: '', text: '' });
  const [feedbackHistory, setFeedbackHistory] = useState([]);
  const [isLoadingHistory, setIsLoadingHistory] = useState(true);
  const [selectedFeedback, setSelectedFeedback] = useState(null);
  const [replyMessage, setReplyMessage] = useState('');
  const [expandedFeedback, setExpandedFeedback] = useState(new Set());
  const [showAllHistory, setShowAllHistory] = useState(false);
  const INITIAL_DISPLAY_COUNT = 3;

  const categories = [
    { value: 'general', label: 'General Feedback' },
    { value: 'bug', label: 'Bug Report' },
    { value: 'feature-request', label: 'Feature Request' },
    { value: 'improvement', label: 'Improvement Suggestion' },
    { value: 'other', label: 'Other' },
  ];

  // Load feedback history
  useEffect(() => {
    loadFeedbackHistory();
  }, []);

  const loadFeedbackHistory = async () => {
    try {
      setIsLoadingHistory(true);
      const response = await api.get('/feedback/my-feedback');
      // Filter out closed feedback from the display
      const activeFeedback = (response.data.feedback || []).filter(
        feedback => feedback.status !== 'closed'
      );
      setFeedbackHistory(activeFeedback);
    } catch (error) {
      console.error('Error loading feedback history:', error);
    } finally {
      setIsLoadingHistory(false);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
    // Clear submit message when user starts typing
    if (submitMessage.text) {
      setSubmitMessage({ type: '', text: '' });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Check authentication before making API call
    if (!checkAuthBeforeAction(navigate)) {
      return;
    }

    // Validation
    if (!formData.subject.trim()) {
      setSubmitMessage({ type: 'error', text: 'Please enter a subject' });
      return;
    }
    if (!formData.message.trim()) {
      setSubmitMessage({ type: 'error', text: 'Please enter your message' });
      return;
    }

    setIsSubmitting(true);
    setSubmitMessage({ type: '', text: '' });

    try {
      await api.post('/feedback', {
        category: formData.category,
        subject: formData.subject,
        message: formData.message,
      });

      setSubmitMessage({
        type: 'success',
        text: 'Thank you for your feedback! We\'ll review it shortly.'
      });

      // Reset form
      setFormData({
        category: 'general',
        subject: '',
        message: '',
      });

      // Reload feedback history
      loadFeedbackHistory();
    } catch (error) {
      console.error('Error submitting feedback:', error);
      setSubmitMessage({
        type: 'error',
        text: error.response?.data?.message || 'Failed to submit feedback. Please try again.'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleReply = async (feedbackId) => {
    if (!replyMessage.trim()) {
      return;
    }

    // Check authentication before making API call
    if (!checkAuthBeforeAction(navigate)) {
      return;
    }

    try {
      await api.post('/feedback', {
        category: 'general',
        subject: `Re: ${selectedFeedback.subject}`,
        message: `Reply to previous feedback:\n\n${replyMessage}`,
      });

      setReplyMessage('');
      setSelectedFeedback(null);
      loadFeedbackHistory();
      setSubmitMessage({
        type: 'success',
        text: 'Your reply has been submitted successfully!'
      });
    } catch (error) {
      console.error('Error submitting reply:', error);
      setSubmitMessage({
        type: 'error',
        text: 'Failed to submit reply. Please try again.'
      });
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusBadge = (status) => {
    const colors = {
      pending: 'bg-yellow-100 text-yellow-800',
      in_progress: 'bg-blue-100 text-blue-800',
      resolved: 'bg-green-100 text-green-800',
      closed: 'bg-gray-100 text-gray-800'
    };
    return colors[status] || colors.pending;
  };

  const toggleExpanded = (feedbackId) => {
    const newExpanded = new Set(expandedFeedback);
    if (newExpanded.has(feedbackId)) {
      newExpanded.delete(feedbackId);
    } else {
      newExpanded.add(feedbackId);
    }
    setExpandedFeedback(newExpanded);
  };

  const displayedFeedback = showAllHistory
    ? feedbackHistory
    : feedbackHistory.slice(0, INITIAL_DISPLAY_COUNT);

  return (
    <div className="flex h-screen bg-gray-50">
      <Sidebar />

      <div className="flex-1 flex flex-col overflow-hidden">
        <Header />

        <div className="flex-1 overflow-y-auto">
          <div className="max-w-4xl mx-auto px-6 py-8">
            <div className="mb-6">
              <h1 className="text-3xl font-bold text-gray-900">Search Banner's Blackboard</h1>
              <p className="text-gray-600 mt-2">
                Share your ideas, report issues, or ask questions. We value your feedback!
              </p>
            </div>

            {/* Feedback Form */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Category Selection */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Category
                  </label>
                  <select
                    name="category"
                    value={formData.category}
                    onChange={handleChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  >
                    {categories.map((cat) => (
                      <option key={cat.value} value={cat.value}>
                        {cat.label}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Subject */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Subject
                  </label>
                  <input
                    type="text"
                    name="subject"
                    value={formData.subject}
                    onChange={handleChange}
                    placeholder="Brief description of your feedback"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    maxLength={200}
                  />
                  <p className="mt-1 text-sm text-gray-500">
                    {formData.subject.length}/200 characters
                  </p>
                </div>

                {/* Message */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Your Message
                  </label>
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    placeholder="Tell us more about your feedback, feature request, or issue..."
                    rows={8}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent resize-none"
                    maxLength={2000}
                  />
                  <p className="mt-1 text-sm text-gray-500">
                    {formData.message.length}/2000 characters
                  </p>
                </div>

                {/* Submit Message */}
                {submitMessage.text && (
                  <div
                    className={`p-4 rounded-lg ${
                      submitMessage.type === 'success'
                        ? 'bg-green-50 text-green-800 border border-green-200'
                        : 'bg-red-50 text-red-800 border border-red-200'
                    }`}
                  >
                    {submitMessage.text}
                  </div>
                )}

                {/* Submit Button */}
                <div className="flex justify-end">
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className={`px-6 py-3 rounded-lg font-medium transition ${
                      isSubmitting
                        ? 'bg-gray-400 text-gray-200 cursor-not-allowed'
                        : 'bg-primary-600 text-white hover:bg-primary-700'
                    }`}
                  >
                    {isSubmitting ? (
                      <span className="flex items-center">
                        <svg
                          className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                        >
                          <circle
                            className="opacity-25"
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            strokeWidth="4"
                          ></circle>
                          <path
                            className="opacity-75"
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                          ></path>
                        </svg>
                        Submitting...
                      </span>
                    ) : (
                      'Submit Feedback'
                    )}
                  </button>
                </div>
              </form>
            </div>

            {/* Guidelines */}
            <div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h3 className="text-sm font-semibold text-blue-900 mb-2">Guidelines</h3>
              <ul className="text-sm text-blue-800 space-y-1 list-disc list-inside">
                <li>Be specific and descriptive in your feedback</li>
                <li>For bug reports, include steps to reproduce the issue</li>
                <li>For feature requests, explain how it would improve your experience</li>
                <li>We review all submissions and will respond to your email if needed</li>
              </ul>
            </div>

            {/* Feedback History */}
            <div className="mt-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Your Feedback History</h2>

              {isLoadingHistory ? (
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 text-center">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
                  <p className="mt-4 text-gray-500">Loading your feedback history...</p>
                </div>
              ) : feedbackHistory.length === 0 ? (
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 text-center">
                  <p className="text-gray-500">You haven't submitted any feedback yet.</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {displayedFeedback.map((feedback) => {
                    const isExpanded = expandedFeedback.has(feedback.id);
                    const messagePreview = feedback.message.length > 150
                      ? feedback.message.substring(0, 150) + '...'
                      : feedback.message;

                    return (
                    <div
                      key={feedback.id}
                      className="bg-white rounded-lg shadow-sm border border-gray-200 hover:shadow-md transition-shadow"
                    >
                      {/* Header - Always Visible */}
                      <div
                        className="p-6 cursor-pointer"
                        onClick={() => toggleExpanded(feedback.id)}
                      >
                        <div className="flex justify-between items-start">
                          <div className="flex-1">
                            <div className="flex items-start gap-3">
                              <h3 className="text-lg font-semibold text-gray-900 flex-1">{feedback.subject}</h3>
                              <button className="text-gray-400 hover:text-gray-600 transition-transform duration-200" style={{ transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)' }}>
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                </svg>
                              </button>
                            </div>
                            <div className="flex items-center gap-3 mt-2 flex-wrap">
                              <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusBadge(feedback.status)}`}>
                                {feedback.status}
                              </span>
                              <span className="px-3 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-700">
                                {feedback.category}
                              </span>
                              <span className="text-sm text-gray-500">
                                {formatDate(feedback.created_at)}
                              </span>
                              {feedback.admin_notes && (
                                <span className="px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800 flex items-center gap-1">
                                  <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                                    <path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z" />
                                    <path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z" />
                                  </svg>
                                  Admin Replied
                                </span>
                              )}
                            </div>
                          </div>
                        </div>

                        {/* Message Preview - Show when collapsed */}
                        {!isExpanded && (
                          <div className="mt-3">
                            <p className="text-sm text-gray-600 line-clamp-2">{messagePreview}</p>
                          </div>
                        )}
                      </div>

                      {/* Expanded Content */}
                      {isExpanded && (
                        <div className="px-6 pb-6">
                          <div className="mb-4 pt-2 border-t border-gray-100">
                            <p className="text-sm text-gray-600 whitespace-pre-wrap">{feedback.message}</p>
                          </div>

                          {/* Admin Reply */}
                          {feedback.admin_notes && (
                            <div className="bg-blue-50 border-l-4 border-blue-500 p-4 rounded">
                              <div className="flex items-start">
                                <div className="flex-shrink-0">
                                  <svg className="h-5 w-5 text-blue-500" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                                  </svg>
                                </div>
                                <div className="ml-3 flex-1">
                                  <p className="text-sm font-medium text-blue-900">Admin Response</p>
                                  <p className="mt-2 text-sm text-blue-800 whitespace-pre-wrap">{feedback.admin_notes}</p>
                                </div>
                              </div>

                              {/* Reply Button */}
                              {selectedFeedback?.id === feedback.id ? (
                                <div className="mt-4" onClick={(e) => e.stopPropagation()}>
                                  <textarea
                                    value={replyMessage}
                                    onChange={(e) => setReplyMessage(e.target.value)}
                                    placeholder="Type your reply here..."
                                    rows={4}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent resize-none"
                                  />
                                  <div className="flex gap-2 mt-2">
                                    <button
                                      onClick={() => handleReply(feedback.id)}
                                      className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition text-sm"
                                    >
                                      Send Reply
                                    </button>
                                    <button
                                      onClick={() => {
                                        setSelectedFeedback(null);
                                        setReplyMessage('');
                                      }}
                                      className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition text-sm"
                                    >
                                      Cancel
                                    </button>
                                  </div>
                                </div>
                              ) : (
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setSelectedFeedback(feedback);
                                  }}
                                  className="mt-3 text-sm text-blue-600 hover:text-blue-800 font-medium"
                                >
                                  Reply to Admin
                                </button>
                              )}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                    );
                  })}

                  {/* Show More/Less Button */}
                  {feedbackHistory.length > INITIAL_DISPLAY_COUNT && (
                    <div className="text-center pt-2">
                      <button
                        onClick={() => setShowAllHistory(!showAllHistory)}
                        className="px-6 py-3 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition font-medium inline-flex items-center gap-2"
                      >
                        {showAllHistory ? (
                          <>
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                            </svg>
                            Show Less
                          </>
                        ) : (
                          <>
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                            </svg>
                            Show All ({feedbackHistory.length - INITIAL_DISPLAY_COUNT} more)
                          </>
                        )}
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Blackboard;
